import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { BackgroundWaves } from '@/components/BackgroundVideo';
import { useSupabase } from '@/hooks/useSupabase';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { DiscoverExperience } from '@/components/DiscoverExperience';
import { DiscoverMode } from '@/services/discoverService';
import { AttachedMedia, AIAgent } from '@/types/aiAgents';
import SidepanelIcon from '../../packages/svgs/SidepanelIcon.tsx';

const Discover = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const { theme } = useTheme();
  const [systemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  const isDarkMode = useMemo(
    () => theme === 'dark' || (theme === 'system' && systemPrefersDark),
    [theme, systemPrefersDark]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;

      setIsHeaderHidden((prev) => {
        if (currentScrollY <= 0) return false;
        if (delta > 3 && currentScrollY > 24) return true;
        if (delta < -3) return false;
        return prev;
      });

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const onToggleSidebarHotkey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || (target as any)?.isContentEditable;
      if (isTyping) return;

      const isCtrlB = e.ctrlKey && (e.key === 'b' || e.key === 'B');
      if (!isCtrlB) return;

      e.preventDefault();
      e.stopPropagation();
      setIsOpen((prev) => !prev);
    };

    window.addEventListener('keydown', onToggleSidebarHotkey, { capture: false });
    return () => window.removeEventListener('keydown', onToggleSidebarHotkey as any);
  }, []);

  const handleContentSelect = useCallback(
    (type: 'weather' | 'news' | 'topic' | 'digest', data: any) => {
      const id = data?.id ?? Math.random().toString(36).slice(2, 10);
      navigate(`/discover/${type}/${id}`, { state: { data } });
    },
    [navigate]
  );

  const handlePromptSubmit = useCallback(async (query: string, mode: DiscoverMode, media?: AttachedMedia | null, agent?: AIAgent | null) => {
    const q = query?.trim();
    if (!q && !media) return;
    
    // Navigate to chat page with message, mode, any attached media, and selected agent
    navigate('/', {
      state: {
        initialMessage: q,
        initialMode: mode,
        initialMedia: media,
        initialAgent: agent,
        fromDiscover: true,
      }
    });
  }, [navigate]);

  return (
    <div className={cn('min-h-screen w-full relative', isDarkMode ? 'dark' : '')}>
      <BackgroundWaves isDarkMode={isDarkMode} className="z-0" />

      <Sidebar
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        onNewChat={() => navigate('/')}
        isDarkMode={isDarkMode}
        onAuth={() => { }}
        user={user}
      />

      <div className="flex flex-col flex-1 relative min-h-screen">
        <header
          className={cn(
            'sticky top-0 z-40 w-full transition-transform duration-200',
            isHeaderHidden ? '-translate-y-full' : 'translate-y-0'
          )}
        >
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2 sm:gap-4 border-grey-200 rounded-lg">
              <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle sidebar"
                className={cn(
                  "p-1 transition-colors duration-150 rounded-xl flex items-center justify-center",
                  isDarkMode
                    ? "text-slate-300 hover:text-white hover:bg-white/10"
                    : "text-black/90 hover:text-slate-900 hover:bg-slate-200/70"
                )}
              >
                <SidepanelIcon
                  className="h-6 w-6"
                  isDarkMode={isDarkMode}
                />
              </button>
            </div>
            <h1
              className={cn(
                'text-2xl instrument-serif-regular',
                isDarkMode ? 'text-white' : 'text-gray-800'
              )}
            >
              Discover
            </h1>
            <div className="w-10" />
          </div>
        </header>

        <main className="flex-1 relative z-10 px-2 xs:px-4 sm:px-6 lg:px-8 pt-8 pb-16 max-w-7xl mx-auto w-full">
          <DiscoverExperience
            isDarkMode={isDarkMode}
            enableStickyInput
            variant="full"
            onContentSelect={handleContentSelect}
            onPromptSubmit={handlePromptSubmit}
          />
        </main>
      </div>
    </div>
  );
};

export default Discover;
